import type { Category, ConsentKitConfig, Decision, ResolvedRegion } from "../types";

/** Render/iteration order. `necessary` is always first and always locked on. */
export const ALL_CATEGORIES: readonly Category[] = [
  "necessary",
  "functional",
  "analytics",
  "marketing",
] as const;

export const OPTIONAL_CATEGORIES = ["functional", "analytics", "marketing"] as const;

/**
 * The pre-decision state: everything optional is off.
 * Art. 7 DSGVO / CJEU C-673/17 — no pre-ticked boxes, ever.
 */
export function deniedDecision(): Decision {
  return { necessary: true, functional: false, analytics: false, marketing: false };
}

export function grantedDecision(enabled: readonly Category[]): Decision {
  const decision = deniedDecision();
  for (const category of enabled) decision[category] = true;
  return decision;
}

/**
 * The state a visitor starts in, before they have touched anything. This is the single
 * place the two regimes diverge, and everything downstream — what the blocker holds back,
 * what Consent Mode defaults to, how the toggles render — reads it from here.
 *
 *  - opt-in   everything optional off. § 25 Abs. 1 TDDDG: no storage or access without
 *             consent, and CJEU C-673/17: nothing pre-ticked.
 *  - opt-out  everything the site enabled is on, minus whatever a Global Privacy Control
 *             signal already took off the table. US state law permits the processing until
 *             the consumer objects, and GPC *is* that objection arriving before the visit.
 */
export function baselineDecision(config: ConsentKitConfig, region: ResolvedRegion): Decision {
  if (region.regime === "opt-in") return deniedDecision();

  const decision = grantedDecision(["necessary", ...config.categories.enabled]);
  if (region.gpc) {
    for (const category of config.optOutCategories) decision[category] = false;
  }
  return decision;
}

/** The categories currently granted — the set the blocker is allowed to release. */
export function grantedCategories(decision: Decision): Category[] {
  return ALL_CATEGORIES.filter((category) => decision[category]);
}

export function isOptional(category: Category): category is Exclude<Category, "necessary"> {
  return category !== "necessary";
}

/** Categories that went from false to true — the set that needs unblocking. */
export function grantedDelta(previous: Decision, next: Decision): Category[] {
  return ALL_CATEGORIES.filter((c) => !previous[c] && next[c]);
}

/** Categories that went from true to false — these require a reload to truly stop. */
export function revokedDelta(previous: Decision, next: Decision): Category[] {
  return ALL_CATEGORIES.filter((c) => previous[c] && !next[c]);
}
